import React, { Component } from 'react'
import classnames from 'classnames'
import StreamInfo from '../../common/stream-info/stream-info'
import SelectBar from '../../common/select-bar/select-bar'
import TagBar from '../../common/tag-bar/tag-bar'
import * as api from '../../../api'
import UserBar from '../../common/user-bar/user-bar'
import Cookies from 'universal-cookie'
import BanBar from '../../common/ban-bar/ban-bar'
import { Helmet } from 'react-helmet'

const scope = 'TeamPage'

const nameSort = (a, b) => sortBy(a.name, b.name)
const gameSort = (a, b) => sortBy(a.game, b.game)
const viewerSort = (a, b) => sortBy(a.viewer_count, b.viewer_count, true)
const timeSort = (a, b) => sortBy(a.time, b.time, true)
const featuredSort = (a, b) => sortBy(a.featured, b.featured, true, true)

const sort = (sortFunction, a, b) =>
  featuredSort(a, b) || (sortFunction ? sortFunction(a, b) : undefined) || nameSort(a, b)
const sortBy = (a, b, numeric = false, reverse = false) =>
  (reverse ? -1 : 1) *
  (a || (numeric ? '0' : '')).toString().localeCompare((b || (numeric ? '0' : '')).toString(), undefined, { numeric })

const sortOptions = [
  {
    icon: <i className='fas fa-id-card-alt' />,
    text: 'Name',
    value: 0,
    title: 'Sort by Name',
  },
  {
    icon: <i className='fas fa-gamepad' />,
    text: 'Game',
    value: 1,
    sortFunction: gameSort,
    title: 'Sort by Game',
  },
  {
    icon: <i className='fa fa-user' />,
    text: 'Viewers',
    value: 2,
    sortFunction: viewerSort,
    title: 'Sort by Viewer Count',
  },
  {
    icon: <i className='far fa-clock' />,
    text: 'Time Live',
    value: 3,
    sortFunction: timeSort,
    title: 'Sort by Time Live',
  },
]

const removeFromArray = (arr, element) => {
  const index = arr.indexOf(element)
  if (index > -1) {
    arr.splice(index, 1)
  }
}

class TeamPage extends Component {
  constructor(props) {
    super(props)
    const prefs = new Cookies().get('preferences') || {
      sortOption: 2,
      includedTags: [],
      excludedTags: [],
      hiddenUsers: [],
    }
    this.state = {
      loading: true,
      cache: Date.now(),
      sortOption: Math.min(3, Math.max(0, prefs.sortOption)),
      includedTags: Array.isArray(prefs.includedTags) ? prefs.includedTags : [],
      excludedTags: Array.isArray(prefs.excludedTags) ? prefs.excludedTags : [],
      hiddenUsers: Array.isArray(prefs.hiddenUsers) ? prefs.hiddenUsers : [],
    }
    this.cache = this.intervalID = undefined
    this.getStreams = this.getStreams.bind(this)
    this.getTags = this.getTags.bind(this)
    this.translateTags = this.translateTags.bind(this)
    this.formatStreamInformation = this.formatStreamInformation.bind(this)
    this.logOut = this.logOut.bind(this)
    this.removeHiddenUser = this.removeHiddenUser.bind(this)
  }

  componentDidMount() {
    this.getStreams(this.getTags)
  }

  componentDidUpdate() {
    const { sortOption, includedTags, excludedTags, hiddenUsers } = this.state
    new Cookies().set(
      'preferences',
      {
        sortOption,
        includedTags,
        excludedTags,
        hiddenUsers,
      },
      {
        maxAge: 2592000,
      },
    )
  }

  componentWillUnmount() {
    if (this.intervalID) {
      clearTimeout(this.intervalID)
    }
  }

  createTitle = pageTitle => {
    const { config } = this.props
    const title = 'Team'

    if (config.siteName && title && pageTitle) {
      return `${title}: ${pageTitle} - ${config.siteName}`
    } else if (config.siteName && title) {
      return `${title} - ${config.siteName}`
    } else if (config.siteName) {
      return config.siteName
    }
  }

  getStreams(callback = () => {}) {
    api.getTeamStreams(this.props.match.params.team).then(({ error, name, logo, banner, info, members }) => {
      this.setState({ loading: false, error, name, logo, banner, info, streams: members }, callback)
    })
  }

  getTags(callback = () => {}) {
    api.getTags().then(tags => this.setState({ tags }, callback))
  }

  logOut() {
    api.logOut().then(this.props.history.go(0))
  }

  translateTags(tags) {
    if (!this.state.tags) {
      return undefined
    }
    return !tags || !Array.isArray(tags)
      ? []
      : tags.map(tag => {
          const tagInfo = this.state.tags.find(info => info.id === tag)
          if (!tagInfo) {
            console.log('[TAGS] Unknown tag: ', tag)
          }
          return { id: tag, text: tagInfo ? tagInfo.name : 'Unknown Tag' }
        })
  }

  translateHiddenUsers() {
    if (!this.state.hiddenUsers || !this.state.streams) {
      return undefined
    }
    const users = this.state.hiddenUsers
      .map(userId => {
        const user = this.state.streams.find(stream => stream.id === userId)
        return user
          ? {
              // icon: <i className='fas fa-user' />,
              title: `Show ${user.name}`,
              name: user.name,
              id: userId,
            }
          : null
      })
      .filter(o => o)
    return users
  }

  removeHiddenUser(userId) {
    this.setState({
      hiddenUsers: this.state.hiddenUsers.filter(id => id !== userId),
    })
  }

  formatStreamInformation(stream) {
    const { includedTags, excludedTags } = this.state
    if (!this.state.tags) {
      const { tags, ...rest } = stream
      return { rest, includedTags, excludedTags }
    }
    const clean = {
      ...stream,
      excludedTags,
      includedTags,
      tags: this.translateTags(stream.tags),
    }
    return clean
  }

  showUser = (id, tags) => {
    const { hiddenUsers, includedTags, excludedTags } = this.state
    const userHidden = hiddenUsers.includes(id)
    const hasRequiredTag = includedTags.length === 0 || includedTags.filter(tagID => tags.includes(tagID)).length > 0
    const hasExcludedTag = excludedTags.some(tagID => tags.includes(tagID))
    return !userHidden && hasRequiredTag && !hasExcludedTag
  }

  onRemoveIncludedTag = id => {
    const newIncludedTags = [...this.state.includedTags]
    removeFromArray(newIncludedTags, id)
    this.setState({
      includedTags: newIncludedTags,
    })
  }

  onRemoveExcludedTag = id => {
    const newExcludedTags = [...this.state.excludedTags]
    removeFromArray(newExcludedTags, id)
    this.setState({
      excludedTags: newExcludedTags,
    })
  }

  onSaveOptions = (id, userIgnore, tagIncludes, tagExcludes) => {
    const newHiddenUsers = [...this.state.hiddenUsers]
    if (userIgnore && !newHiddenUsers.includes(id)) {
      newHiddenUsers.push(id)
    }
    const newIncludedTags = [...this.state.includedTags]
    tagIncludes.forEach(id => {
      if (newIncludedTags.includes(id)) {
        removeFromArray(newIncludedTags, id)
      } else {
        newIncludedTags.push(id)
      }
    })
    tagExcludes.forEach(id => removeFromArray(newIncludedTags, id))
    const newExcludedTags = [...this.state.excludedTags]
    tagExcludes.forEach(id => {
      if (newExcludedTags.includes(id)) {
        removeFromArray(newExcludedTags, id)
      } else {
        newExcludedTags.push(id)
      }
    })
    tagIncludes.forEach(id => removeFromArray(newExcludedTags, id))
    this.setState({
      hiddenUsers: newHiddenUsers,
      includedTags: newIncludedTags,
      excludedTags: newExcludedTags,
    })
  }

  renderStreams = () => {
    const { loading, error, sortOption, streams, cache } = this.state
    const filteredStreams = !streams ? null : streams.filter(stream => stream.showStream)
    if (loading) {
      return <div className={`${scope}-Loading`}>Loading...</div>
    }
    if (error) {
      return <div className={`${scope}-Error`}>Sorry, we couldn't find a team with that name.</div>
    }
    if (!(filteredStreams && filteredStreams.length)) {
      return <div className={`${scope}-Error`}>Sorry, we couldn't find any members for that team.</div>
    }
    if (filteredStreams && filteredStreams.length) {
      const onlineMembers = !filteredStreams
        ? null
        : filteredStreams
            .filter(stream => stream.online)
            .sort((a, b) => sort(sortOptions[sortOption].sortFunction, a, b))
      const offlineMembers = !filteredStreams
        ? null
        : filteredStreams
            .filter(stream => !stream.online)
            .sort((a, b) => sort(sortOptions[sortOption].sortFunction, a, b))

      return (
        <div>
          {onlineMembers ? (
            <div className={`${scope}-StreamInfoContainer`}>
              {onlineMembers.map(stream =>
                this.showUser(stream.id, stream.tags || []) ? (
                  <StreamInfo
                    cache={cache}
                    blocked={!this.showUser(stream.id, stream.tags || [])}
                    onSaveOptions={this.onSaveOptions}
                    key={stream.login}
                    featuredTitle={stream.featured && `${this.props.config.siteName} Partner Streamer`}
                    {...this.formatStreamInformation(stream)}
                  />
                ) : null,
              )}
            </div>
          ) : null}
          {offlineMembers ? (
            <div className={`${scope}-StreamInfoContainer`}>
              {offlineMembers.map(stream =>
                this.showUser(stream.id, stream.tags || []) ? (
                  <StreamInfo
                    cache={cache}
                    blocked={!this.showUser(stream.id, stream.tags || [])}
                    onSaveOptions={this.onSaveOptions}
                    key={stream.login}
                    featuredTitle={stream.featured && `${this.props.config.siteName} Partner Streamer`}
                    {...this.formatStreamInformation(stream)}
                  />
                ) : null,
              )}
            </div>
          ) : null}
        </div>
      )
    }
  }

  renderHeaderBars = () => {
    const { loading, error, sortOption, includedTags, excludedTags, streams } = this.state
    if (loading || error || !streams || !streams.length) {
      return null
    }
    return (
      <div className={classnames(`${scope}-Filters`)}>
        <div className={classnames(`${scope}-Selectors`)}>
          <SelectBar
            inline
            title='Sort:'
            options={sortOptions}
            value={sortOption}
            onSelect={value => this.setState({ sortOption: value })}
          />
        </div>
        <div>
          {includedTags.length > 0 && this.state.tags ? (
            <TagBar
              onRemove={this.onRemoveIncludedTag}
              title='Required Tags:'
              tags={this.translateTags(includedTags)}
            />
          ) : null}
        </div>
        <div>
          {excludedTags.length > 0 && this.state.tags ? (
            <TagBar
              onRemove={this.onRemoveExcludedTag}
              title='Excluded Tags:'
              tags={this.translateTags(excludedTags)}
            />
          ) : null}
        </div>
      </div>
    )
  }

  renderBanBar = () => {
    const { loading, error, streams, hiddenUsers } = this.state
    if (loading || error || !streams || !streams.length) {
      return null
    }
    return (
      <div className={classnames(`${scope}-Bans`)}>
        {hiddenUsers && hiddenUsers.length > 0 && streams && (
          <BanBar
            title='Hidden Users:'
            options={this.translateHiddenUsers().sort(nameSort)}
            onSelect={this.removeHiddenUser}
          />
        )}
      </div>
    )
  }

  renderTitle = () => {
    const { loading, error, name } = this.state

    if (loading) {
      return null
    }
    if (error || !name) {
      return null
    }
    return (
      <Helmet>
        <title>{this.createTitle(name)}</title>
      </Helmet>
    )
  }

  renderLogo = () => {
    const { logo, name } = this.state
    if (!logo) {
      return null
    }
    return <img src={logo} alt={name || ''} className={`${scope}-Logo`} />
  }

  renderBanner = () => {
    const { banner, name } = this.state
    if (!banner) {
      return null
    }
    return <img src={banner} alt={name || ''} className={`${scope}-Banner`} />
  }

  renderInfo = () => {
    const { info, name } = this.state
    const cleanEnd = arr => {
      while (arr && arr.length && !arr[arr.length - 1]) {
        arr.pop()
      }
      return arr
    }

    if (!info) {
      return null
    }
    const cleanInfo = cleanEnd(info.replace(/(<([^>]+)>)/gi, '').split('\n'))
    return cleanInfo && cleanInfo.length ? (
      <div className={`${scope}-Info`}>
        {name ? <h1>{name}</h1> : null}
        <h2>
          {cleanInfo.map(line => (
            <p>{line}</p>
          ))}
        </h2>
      </div>
    ) : null
  }

  render() {
    const { className, user } = this.props

    return (
      <div className={classnames(scope, className)}>
        {this.renderTitle()}
        <div className={classnames(`${scope}-Header`)}>
          <UserBar user={user} onLogOut={this.logOut} />
          {this.renderHeaderBars()}
        </div>
        <div className={classnames(`${scope}-InfoContainer`)}>
          {this.renderBanner()}
          {this.renderInfo()}
        </div>
        {this.renderStreams()}
        {this.renderBanBar()}
      </div>
    )
  }
}

export default TeamPage



// WEBPACK FOOTER //
// ./src/components/views/team-page/team-page.jsx